// ---------------------------------------------------------------------------
// Units selector (area×runoff → volume), equivalent to the "Tools" tab panel
// of the original (Tools.vb) that edits APO_UNIDADES. Unlike the 2 aquifer
// checkboxes of the calculation dialog (acuiferosNegativos/modelarBombeos,
// see calculationOptions.ts), this IS a real value the engine uses (see
// src/engine/aggregation.ts, F.acumula_apo/Ac.acumula_acu in Evalhid.dll) —
// confirmed by decompiling the DLL: `G.unid` (the coefficient) really gets
// multiplied into every aggregated contribution/infiltration per outlet
// point/aquifer.
//
// Always exactly 3 rows — never added or removed, only one gets marked
// `checked` and, if it's the custom one (code=10), its name/value gets
// edited. Same codes as the original.
// ---------------------------------------------------------------------------
import { TranslatableError } from "../i18n/translate.ts";
import type { EvalHidProject, UnitOption } from "./types.ts";

export const UNITS_KM2 = 0;
export const UNITS_HA = 1;
export const UNITS_OTHER = 10;

/** "1 km² × 1mm = 0.001 Hm³" — the active row by default in a new project (same seed as MPE_APO_base.sehd, the original's template). */
export const DEFAULT_UNIT_OPTIONS: UnitOption[] = [
  { code: UNITS_KM2, checked: true, units: "Hm3", value: 0.001 },
  { code: UNITS_HA, checked: false, units: "Hm3", value: 0.00001 },
  { code: UNITS_OTHER, checked: false, units: "Otras", value: 0 },
];

/**
 * Validates `rows` really has the shape engine/aggregation.ts and the UI
 * assume (exactly 3 rows, exactly one checked) — falls back to
 * `DEFAULT_UNIT_OPTIONS` entirely if it doesn't, rather than accepting a
 * malformed array silently. The one real place this could actually happen:
 * `ehd.ts`'s `mapUnitOptions` reads a real .ehd's APO_UNIDADES table
 * directly, with no validation of its own — a corrupted/hand-edited row
 * count, or two rows both marked checked, would otherwise flow straight
 * into `EvalHidProject.unitOptions` unchecked.
 */
export function buildUnitOptions(rows: UnitOption[]): UnitOption[] {
  if (rows.length !== 3) return DEFAULT_UNIT_OPTIONS;
  if (rows.filter((r) => r.checked).length !== 1) return DEFAULT_UNIT_OPTIONS;
  return rows;
}

/** The active row's coefficient — what engine/aggregation.ts actually uses. If none is marked (corrupt data), falls back to the default km² value. */
export function activeUnitsCoefficient(project: EvalHidProject): number {
  return project.unitOptions.find((o) => o.checked)?.value ?? DEFAULT_UNIT_OPTIONS[0].value;
}

/** Cosmetic only: renders a literal "Hm3" as "Hm³" (superscript 3) for display — doesn't touch the stored value, since it's read-only UI labeling anyway (see activeUnitName below). Real .ehd files (and this app's own default seed) store the digit as plain "3", not the Unicode superscript. */
function withSuperscriptDigit(name: string): string {
  return name.replace(/Hm3\b/g, "Hm³");
}

/** Name of the active row's resulting volume (e.g. "Hm³") — for UI labeling only, the original never uses it in any export (see CLAUDE.md). */
export function activeUnitName(project: EvalHidProject): string {
  const name = project.unitOptions.find((o) => o.checked)?.units ?? DEFAULT_UNIT_OPTIONS[0].units;
  return withSuperscriptDigit(name);
}

function mark(options: UnitOption[], code: number): UnitOption[] {
  return options.map((o) => ({ ...o, checked: o.code === code }));
}

export function chooseUnitsKm2(project: EvalHidProject): EvalHidProject {
  return { ...project, unitOptions: mark(project.unitOptions, UNITS_KM2) };
}

export function chooseUnitsHa(project: EvalHidProject): EvalHidProject {
  return { ...project, unitOptions: mark(project.unitOptions, UNITS_HA) };
}

/** Same as the original (Tools.vb): no validation on the number beyond being positive — the user is free to name their volume however they like. */
export function chooseCustomUnits(
  project: EvalHidProject,
  name: string,
  value: number
): EvalHidProject {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("units.emptyNameError");
  if (!(value > 0)) throw new TranslatableError("units.invalidCoefficientError");
  return {
    ...project,
    unitOptions: project.unitOptions.map((o) =>
      o.code === UNITS_OTHER
        ? { ...o, checked: true, units: cleanName, value }
        : { ...o, checked: false }
    ),
  };
}
